import { isUUID, isString } from 'class-validator';
import { debounce } from 'debounce';
import io from 'socket.io-client';

import config from '@/config/config';
import { UserRole, databaseActions } from '@nova/core';
import { hasCollapsedSocketEventIds } from '@satellite/helpers/socketEventMatch';
import { LAST_CRUD_ACTION_KEY } from '@satellite/plugins/util';

/** @typedef {{ skipSelfMadeEventCheck?: boolean | (() => boolean) }} SocketPluginOptions */

class SocketPlugin {
  socket;
  store;
  app;
  eventsBound = false;
  // Had to reduce this delay from 500 due to it being user-perceptable.
  debounceDelayMs = 100;
  previousEventKey = null;
  debounceData = {
    count: 0,
    ids: [],
    payloads: []
  };

  actions = databaseActions;

  entities = [
    'Appointment',
    'AssetContainer',
    'AssetContainerEvent',
    'AssetVisit',
    'AssetVisitEvent',
    'Bol',
    'Comment',
    'CompanyIdentifier',
    'CustomFormData',
    'Dock',
    'Field',
    'Flow',
    'FormField',
    'LoadType',
    'MessageThread',
    'MessageThreadMessage',
    'MessageThreadMessageTranslation',
    'Org',
    'OrgCarrierSettings',
    'Spot',
    'SpotArea',
    'SpotAssignment',
    'SpotReserve',
    'Trigger',
    'User',
    'VehicleObservation',
    'Warehouse',
    'WarehouseGroup',
    'YardTask',
    'YardTaskCheckAssetContainer',
    'YardTaskEvent'
  ];

  esIndexes = ['vehicle-observation'];
  esOps = ['index', 'update', 'delete'];

  connectionStatuses = {
    CONNECTED: 'connected',
    RECONNECTING: 'reconnecting',
    DISCONNECTED: 'disconnected'
  };

  // These entities will react on socket events when the
  // logged user role matches with the key.
  // When the role is not in the list, all entities
  // will be used.
  userEntities = {
    [UserRole.GOD]: ['Org', 'User'],
    [UserRole.INTERNAL]: ['Org', 'User']
  };

  /**
   * @param {import('vue').App} app
   * @param {SocketPluginOptions} [options]
   */
  constructor(app, options = {}) {
    this.app = app;
    this.store = app.config.globalProperties.$store;

    /** @type {boolean | (() => boolean)} */
    this.skipSelfMadeEventCheck = options.skipSelfMadeEventCheck ?? false;
    this.debounceEvent = debounce(this.handleSocketEvent.bind(this), this.debounceDelayMs);
  }

  connect(token) {
    this.disconnect();

    if (!token) {
      return;
    }

    this.socket = io(`${config.subspace_url}`, {
      transports: ['websocket'],
      auth: { token }
    });
    this.bindConnectEvent();
    this.socket.connect();
  }

  disconnect() {
    if (this.socket?.connected) {
      this.socket.disconnect();
      this.emitConnectionStatusEvent(this.connectionStatuses.DISCONNECTED);
      this.eventsBound = false;
    }
  }

  bindConnectEvent() {
    this.socket.once('connect', () => {
      this.emitConnectionStatusEvent(this.connectionStatuses.CONNECTED);
      if (this.socket.connected && !this.eventsBound) {
        this.bindEvents();
      }
    });

    // Handle Disconnection Events
    for (const event of ['error', 'reconnect_error', 'reconnect_failed']) {
      this.socket.on(event, () =>
        this.emitConnectionStatusEvent(this.connectionStatuses.DISCONNECTED)
      );
    }

    this.socket.on('reconnect', () =>
      this.emitConnectionStatusEvent(this.connectionStatuses.CONNECTED)
    );

    this.socket.on('reconnect_attempt', () =>
      this.emitConnectionStatusEvent(this.connectionStatuses.RECONNECTING)
    );
  }

  getActionDisplayName(action) {
    return this.actions[action].displayName;
  }

  getActionPastTense(action) {
    return this.actions[action].pastTense;
  }

  // Build the topic variant the backend sends for DB-triggered events (Postgres uses table name
  // with only first letter capitalized, e.g. "Spotassignment" not "SpotAssignment").
  static tableNameTopicVariant(action, entity) {
    if (!entity || typeof entity !== 'string') {
      return undefined;
    }
    const tableStyleEntity = entity.charAt(0) + entity.slice(1).toLowerCase();
    return `${action}-${tableStyleEntity}`;
  }

  // Allow socket events to be passed through the global "eventHub"
  // Notice the payload.id on the event key, this allows specific detail pages to respond to changes to the entity they own
  // Without having to perform logic to detect if they are the owner
  bindEvents() {
    const events = this.getBindableEvents();

    events.forEach(event => {
      const onPayload = payload => {
        if (event.eventKey !== this.previousEventKey) {
          this.debounceEvent.flush();
          this.resetDebouncing();
        }

        this.debounceData.count++;
        this.debounceData.ids.push(payload.id);
        if (event.entity === 'Appointment') {
          this.debounceData.payloads.push(payload);
        }
        this.debounceEvent(event, payload);

        this.previousEventKey = event.eventKey;
      };

      this.socket.on(event.eventKey, onPayload);
      // Backend DB triggers emit topic with table-name casing (e.g. delete-Spotassignment).
      // Subscribe to that variant so we receive the event and forward with canonical eventKey.
      const tableTopic = SocketPlugin.tableNameTopicVariant(event.action, event.entity);
      if (tableTopic && tableTopic !== event.eventKey) {
        this.socket.on(tableTopic, onPayload);
      }
    });

    // Pass the websocket heartbeat through the EventHub
    this.socket.on('heartbeat', payload => {
      this.app.config.globalProperties.$eventHub.$emit('heartbeat', payload);
    });

    this.eventsBound = true;
  }

  emitConnectionStatusEvent(status) {
    this.app.config.globalProperties.$eventHub.$emit('websocket-connection-status-change', status);
  }

  resetDebouncing() {
    this.debounceData = {
      count: 0,
      ids: [],
      payloads: []
    };
  }

  handleSocketEvent(event, payload) {
    // Events happening on these entities are
    // propagated without the $eventHub and depends on the
    // store method implementation behavior to react
    // to the event.
    const globalEventEntities = ['Org'];

    if (this.debounceData.count === 1) {
      this.resetDebouncing();
    }

    payload.debounce = this.debounceData;
    payload.action = event.action;

    // This event is meant for debugging purposes, ONLY
    this.app.config.globalProperties.$eventHub.$emit('subspace-event', { payload, event });
    if (this.isEventAllowed(event, payload)) {
      this.app.config.globalProperties.$eventHub.$emit(`${event.eventKey}-${payload.id}`, payload);
      this.app.config.globalProperties.$eventHub.$emit(`${event.eventKey}`, payload);
    }

    // This calls straight the store method called handleSocketEvent
    // which bypasses the event hub, meant for entities that should
    // notify on all frontend modules for changes
    if (this.storeModulesSupportEvent(event)) {
      if (globalEventEntities.includes(event.entity)) {
        this.store.dispatch(`${event.entity}s/handleSocketEvent`, {
          action: event.action,
          data: payload
        });
      }
    }

    this.resetDebouncing();
    this.previousEventKey = null;
  }

  storeModulesSupportEvent(event) {
    return (
      this.store.modules()[`${event.entity}s`] &&
      this.store.modules()[`${event.entity}s`]['actions']['handleSocketEvent']
    );
  }

  /**
   * Resolves the self-made-event opt-out. A boolean is taken as given; a function is
   * called per event, so a host app can decide late — from a feature flag, an env
   * condition or store state that is not known at plugin construction.
   *
   * A throwing resolver keeps the check on: opting out on a failed read would let the
   * session's own echoes through, which is the louder failure of the two.
   * @returns {boolean}
   */
  resolveSkipSelfMadeEventCheck() {
    if (typeof this.skipSelfMadeEventCheck !== 'function') {
      return Boolean(this.skipSelfMadeEventCheck);
    }

    try {
      return Boolean(this.skipSelfMadeEventCheck());
    } catch {
      return false;
    }
  }

  /*
   * Check if the event is a self-made event
   * When the logged user has performed the action
   * that triggered the event.
   */
  isSelfMadeEvent(event, entityId, lastChangedBy) {
    let isSelfMade = false;

    const lastCrudAction = sessionStorage.getItem(LAST_CRUD_ACTION_KEY) || ':';
    const [lastCrudEventName, lastCrudEventID] = lastCrudAction.split(':');

    const hasLastEvent = isString(lastCrudEventName) && isUUID(lastCrudEventID) && isUUID(entityId);

    if (hasLastEvent) {
      isSelfMade =
        entityId === lastCrudEventID &&
        event.eventKey.toLowerCase() === lastCrudEventName.toLowerCase() &&
        this.store.state.Auth.me?.id === lastChangedBy;
    }

    return isSelfMade;
  }

  isEventAllowed(event, payload) {
    if (!isUUID(this.store.state.Auth.me?.id)) {
      return false;
    }

    // A collapsed payload stands in for every id it swallowed, so a self-made last arrival must
    // still be delivered or the other entities' updates are lost with it.
    if (
      !this.resolveSkipSelfMadeEventCheck() &&
      !hasCollapsedSocketEventIds(payload) &&
      this.isSelfMadeEvent(event, payload.id, payload.lastChangedBy)
    ) {
      return false;
    }

    const allowedEntities = this.userEntities[this.store.state.Auth.me?.role] ?? [
      ...this.entities,
      ...this.esIndexes
    ];
    return allowedEntities.includes(event.entity);
  }

  getBindableEvents(entityFilter = null) {
    const events = [];

    this.entities.forEach(entity => {
      if (!entityFilter || entity === entityFilter) {
        Object.keys(this.actions).forEach(action => {
          events.push({ eventKey: `${action}-${entity}`, action, entity });
        });
      }
    });

    this.esIndexes.forEach(index => {
      this.esOps.forEach(operation => {
        events.push({ eventKey: `${index}:${operation}`, entity: index, action: operation });
      });
    });

    return events;
  }
}

export default SocketPlugin;
